// Bridge to the Android UsbPrinterPlugin (android/.../UsbPrinterPlugin.java).
// The Capacitor WebView has no WebUSB, so on the native app the USB printer
// transport goes through this plugin instead of navigator.usb.

import { Capacitor, registerPlugin } from '@capacitor/core';

export interface NativeUsbDevice {
  /** Android's /dev/bus/usb path — the only id that separates two printers of
   *  the same model (identical vendor/product ids). */
  deviceName?: string;
  vendorId: number;
  productId: number;
  productName?: string;
  manufacturerName?: string;
  hasPermission: boolean;
  /** True when the device exposes a USB printer-class interface. */
  isPrinter: boolean;
}

interface UsbPrinterNativePlugin {
  list(): Promise<{ devices: NativeUsbDevice[] }>;
  requestPermission(opts: {
    vendorId: number;
    productId: number;
    deviceName?: string;
  }): Promise<{ granted: boolean; deviceName?: string }>;
  print(opts: {
    vendorId: number;
    productId: number;
    deviceName?: string;
    data: string;
  }): Promise<void>;
}

const UsbPrinterNative = registerPlugin<UsbPrinterNativePlugin>('UsbPrinter');

// True when running inside the Android (or iOS) Capacitor shell.
export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform();
}

export async function nativeListDevices(): Promise<NativeUsbDevice[]> {
  const { devices } = await UsbPrinterNative.list();
  return devices;
}

// Shows the Android "Allow access to the USB device?" system dialog (no-op
// resolve when permission was already granted).
export async function nativeRequestPermission(
  vendorId: number,
  productId: number,
  deviceName?: string
): Promise<boolean> {
  const { granted } = await UsbPrinterNative.requestPermission({ vendorId, productId, deviceName });
  return granted;
}

export async function nativePrint(
  vendorId: number,
  productId: number,
  data: Uint8Array,
  deviceName?: string
): Promise<void> {
  await UsbPrinterNative.print({ vendorId, productId, deviceName, data: toBase64(data) });
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  const CHUNK = 0x8000; // String.fromCharCode arg limit safety
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...Array.from(bytes.subarray(i, i + CHUNK)));
  }
  return btoa(binary);
}
